import React, { useState, useEffect } from 'react';
import { useApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  CalendarRange, Plus, Send, Clock, CheckCircle, AlertTriangle, Eye, 
  Play, BarChart3, FileSpreadsheet, Sparkles, ChevronRight, RefreshCw, Mail,
  Settings2
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { toast } from 'sonner';
import FormBuilder from '../components/FormBuilder';

const STATUS_COLORS = {
  "Rascunho": "bg-slate-500",
  "Ativa": "bg-green-500",
  "Encerrada": "bg-blue-500",
  "Cancelada": "bg-red-500"
};

const MAGNITUDE_COLORS = {
  "Baixa": "#22C55E",
  "Média": "#F59E0B",
  "Alta": "#F97316",
  "Crítica": "#EF4444"
};

export default function CampanhasPage() {
  const api = useApi();
  const [campanhas, setCampanhas] = useState([]);
  const [validacoesPendentes, setValidacoesPendentes] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
  const [validacaoModalOpen, setValidacaoModalOpen] = useState(false);
  const [formBuilderModalOpen, setFormBuilderModalOpen] = useState(false);
  const [selectedCampanha, setSelectedCampanha] = useState(null);
  const [selectedSubmissao, setSelectedSubmissao] = useState(null);
  const [campanhaDashboard, setCampanhaDashboard] = useState(null);
  const [submissaoDiferencas, setSubmissaoDiferencas] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    nome: '',
    ano_referencia: new Date().getFullYear(),
    tipo: 'Anual',
    data_inicio: new Date().toISOString().split('T')[0],
    data_limite: '',
    instituicoes_alvo: 'todas',
    filtro_distrito_ids: [],
    campos_personalizados: []
  });
  
  // Form Builder campos for editing existing campaign
  const [camposPersonalizados, setCamposPersonalizados] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [campData, validData, distData] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campanhas`, { headers }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campanhas/validacoes/pendentes`, { headers }).then(r => r.json()).catch(() => []),
        api.getDistritos()
      ]);
      setCampanhas(Array.isArray(campData) ? campData : []);
      setValidacoesPendentes(Array.isArray(validData) ? validData : []);
      setDistritos(distData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampanha = async () => {
    if (!createForm.nome || !createForm.data_limite) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campanhas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });
      
      if (!response.ok) throw new Error('Erro ao criar campanha');
      
      toast.success('Campanha criada com sucesso!');
      setCreateModalOpen(false);
      setCreateForm({
        nome: '',
        ano_referencia: new Date().getFullYear(),
        tipo: 'Anual',
        data_inicio: new Date().toISOString().split('T')[0],
        data_limite: '',
        instituicoes_alvo: 'todas',
        filtro_distrito_ids: [],
        campos_personalizados: []
      });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao criar campanha');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAtivarCampanha = async (campanhaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campanhas/${campanhaId}/ativar`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erro ao ativar');
      toast.success('Campanha ativada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao ativar campanha');
    }
  };

  const openDashboard = async (campanha) => {
    setSelectedCampanha(campanha);
    setDashboardModalOpen(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campanhas/${campanha.id}/dashboard`);
      const data = await response.json();
      setCampanhaDashboard(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    }
  };

  const openFormBuilder = async (campanha) => {
    setSelectedCampanha(campanha);
    setCamposPersonalizados(campanha.campos_personalizados || []);
    setFormBuilderModalOpen(true);
  };

  const saveFormBuilderCampos = async () => {
    if (!selectedCampanha) return;
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanhas/${selectedCampanha.id}/campos-personalizados`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(camposPersonalizados)
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao guardar campos');
      }
      
      toast.success('Campos personalizados guardados com sucesso!');
      setFormBuilderModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving campos:', error);
      toast.error(error.message || 'Erro ao guardar campos');
    } finally {
      setFormLoading(false);
    }
  };

  const openValidacao = async (submissao) => {
    setSelectedSubmissao(submissao);
    setValidacaoModalOpen(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissoes/${submissao.submissao_id}/diferencas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Failed to load differences');
      }
      const data = await response.json();
      setSubmissaoDiferencas(data);
    } catch (error) {
      console.error('Error loading differences:', error);
      toast.error('Erro ao carregar diferenças');
    }
  };

  const handleAprovarDiferenca = async (diferencaId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diferencas/${diferencaId}/aprovar`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Diferença aprovada');
      openValidacao(selectedSubmissao); // Refresh
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const handleRejeitarDiferenca = async (diferencaId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diferencas/${diferencaId}/rejeitar`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Diferença rejeitada');
      openValidacao(selectedSubmissao);
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  const handleAprovarTodasIA = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissoes/${selectedSubmissao.submissao_id}/aprovar-todas-ia`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      openValidacao(selectedSubmissao);
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const handleAplicarAlteracoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissoes/${selectedSubmissao.submissao_id}/aplicar-alteracoes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      setValidacaoModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao aplicar alterações');
    }
  };

  // Stats
  const campanhasAtivas = campanhas.filter(c => c.status === 'Ativa').length;
  const totalPendentes = validacoesPendentes.length;
  const totalCriticas = Array.isArray(validacoesPendentes) 
    ? validacoesPendentes.reduce((sum, v) => sum + (v.num_criticas || 0), 0)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="campanhas-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campanhas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Campanhas de Atualização</h1>
          <p className="text-slate-500 mt-1">Gestão de campanhas anuais com validação inteligente por IA</p>
        </div>
        <Button 
          onClick={() => setCreateModalOpen(true)}
          className="bg-[#0066CC] hover:bg-[#0052a3]"
          data-testid="create-campanha-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Campanha
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Campanhas Ativas</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{campanhasAtivas}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CalendarRange className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Validações Pendentes</p>
                <p className="text-3xl font-bold text-amber-600 mt-2">{totalPendentes}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Diferenças Críticas</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{totalCriticas}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Campanhas</p>
                <p className="text-3xl font-bold text-[#0066CC] mt-2">{campanhas.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-[#0066CC]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="validacoes" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="validacoes" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Validações Pendentes
            {totalPendentes > 0 && <Badge className="bg-amber-500 ml-1">{totalPendentes}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="campanhas">Campanhas</TabsTrigger>
        </TabsList>

        <TabsContent value="validacoes">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Submissões por Validar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {validacoesPendentes.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p>Todas as submissões foram validadas!</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Instituição</TableHead>
                      <TableHead>Campanha</TableHead>
                      <TableHead className="text-center">Diferenças</TableHead>
                      <TableHead className="text-center">Críticas</TableHead>
                      <TableHead className="text-center">Aprovadas IA</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validacoesPendentes.map((sub) => (
                      <TableRow key={sub.submissao_id} className="hover:bg-slate-50">
                        <TableCell>
                          <p className="font-medium">{sub.instituicao_nome}</p>
                          <p className="text-xs text-slate-500">{sub.instituicao_codigo}</p>
                        </TableCell>
                        <TableCell>{sub.campanha_nome}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{sub.num_diferencas}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {sub.num_criticas > 0 ? (
                            <Badge className="bg-red-500">{sub.num_criticas}</Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-green-500">{sub.num_aprovadas_ia}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {sub.data_submissao ? new Date(sub.data_submissao).toLocaleDateString('pt-PT') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            onClick={() => openValidacao(sub)}
                            className="bg-[#0066CC] hover:bg-[#0052a3]"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Validar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campanhas" data-testid="tab-campanhas-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <CalendarRange className="w-5 h-5 text-[#0066CC]" />
                Campanhas de Atualização
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table data-testid="campanhas-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Campanha</TableHead>
                    <TableHead>Ano</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-center">Instituições</TableHead>
                    <TableHead className="text-center">Taxa Resposta</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campanhas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        Nenhuma campanha criada
                      </TableCell>
                    </TableRow>
                  ) : (
                    campanhas.map((camp) => (
                      <TableRow key={camp.id} className="hover:bg-slate-50" data-testid={`campanha-row-${camp.id}`}>
                        <TableCell>
                          <p className="font-medium">{camp.nome}</p>
                          <p className="text-xs text-slate-500">{camp.codigo}</p>
                        </TableCell>
                        <TableCell>{camp.ano_referencia}</TableCell>
                        <TableCell className="text-sm">
                          {camp.data_inicio} a {camp.data_limite}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-bold text-[#0066CC]">{camp.num_instituicoes_respondidas || 0}</span>
                          <span className="text-slate-400">/{camp.num_instituicoes_alvo}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Progress value={camp.taxa_resposta || 0} className="w-16 h-2" />
                            <span className="text-sm font-medium">{camp.taxa_resposta || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[camp.status]} text-white`}>
                            {camp.status}
                          </Badge>
                          {camp.campos_personalizados?.length > 0 && (
                            <Badge variant="outline" className="ml-2 text-purple-600 border-purple-200">
                              {camp.campos_personalizados.length} campos
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {camp.status === 'Rascunho' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openFormBuilder(camp)}
                                  className="text-purple-600"
                                  title="Configurar Campos Personalizados"
                                  data-testid={`form-builder-campanha-${camp.id}`}
                                >
                                  <Settings2 className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleAtivarCampanha(camp.id)}
                                  className="text-green-600"
                                  data-testid={`ativar-campanha-${camp.id}`}
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openDashboard(camp)}
                              data-testid={`dashboard-campanha-${camp.id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Campaign Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Campanha de Atualização</DialogTitle>
            <DialogDescription>Criar uma nova campanha para recolha de dados das instituições</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Campanha *</Label>
              <Input
                value={createForm.nome}
                onChange={(e) => setCreateForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Atualização Anual 2025"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ano de Referência</Label>
                <Input
                  type="number"
                  value={createForm.ano_referencia}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, ano_referencia: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={createForm.tipo} 
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Anual">Anual</SelectItem>
                    <SelectItem value="Semestral">Semestral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={createForm.data_inicio}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data Limite *</Label>
                <Input
                  type="date"
                  value={createForm.data_limite}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, data_limite: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Instituições Alvo</Label>
              <Select 
                value={createForm.instituicoes_alvo} 
                onValueChange={(v) => setCreateForm(prev => ({ ...prev, instituicoes_alvo: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as instituições ativas</SelectItem>
                  <SelectItem value="por_distrito">Por distrito</SelectItem>
                  <SelectItem value="customizado">Seleção personalizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {createForm.instituicoes_alvo === 'por_distrito' && (
              <div>
                <Label>Distritos</Label>
                <Select 
                  value={createForm.filtro_distrito_ids[0] || ''} 
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, filtro_distrito_ids: [v] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {distritos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCampanha} className="bg-[#0066CC] hover:bg-[#0052a3]" disabled={formLoading}>
              {formLoading ? 'A criar...' : 'Criar Campanha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Dashboard Modal */}
      <Dialog open={dashboardModalOpen} onOpenChange={setDashboardModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0066CC]" />
              Dashboard: {selectedCampanha?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {campanhaDashboard && (
            <div className="space-y-6 py-4">
              {/* KPIs */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-[#0066CC]">{campanhaDashboard.kpis.respondidas}</p>
                  <p className="text-sm text-slate-500">Respondidas</p>
                  <Progress value={campanhaDashboard.kpis.taxa_resposta} className="mt-2 h-2" />
                  <p className="text-xs text-slate-400 mt-1">{campanhaDashboard.kpis.taxa_resposta}%</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{campanhaDashboard.kpis.por_validar}</p>
                  <p className="text-sm text-slate-500">Por Validar</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{campanhaDashboard.kpis.aprovadas}</p>
                  <p className="text-sm text-slate-500">Aprovadas</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{campanhaDashboard.kpis.taxa_aprovacao_ia}%</p>
                  <p className="text-sm text-slate-500">Aprovação IA</p>
                </div>
              </div>
              
              {/* Differences by Magnitude */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Diferenças por Magnitude</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      {campanhaDashboard.kpis?.diferencas_por_magnitude ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(campanhaDashboard.kpis.diferencas_por_magnitude).map(([name, value]) => ({ name, value }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {Object.entries(campanhaDashboard.kpis.diferencas_por_magnitude).map(([name], index) => (
                                <Cell key={`cell-${index}`} fill={MAGNITUDE_COLORS[name]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-400">
                          Sem dados de magnitude
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Instituições sem Resposta ({campanhaDashboard.nao_responderam?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[200px] overflow-y-auto space-y-2">
                      {(campanhaDashboard.nao_responderam || []).slice(0, 5).map((inst) => (
                        <div key={inst.instituicao_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div>
                            <p className="text-sm font-medium">{inst.instituicao_nome}</p>
                            <p className="text-xs text-slate-500">{inst.instituicao_codigo}</p>
                          </div>
                          <Button size="sm" variant="ghost">
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Validation Modal */}
      <Dialog open={validacaoModalOpen} onOpenChange={setValidacaoModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Validação: {selectedSubmissao?.instituicao_nome}
            </DialogTitle>
            <DialogDescription>{selectedSubmissao?.instituicao_codigo}</DialogDescription>
          </DialogHeader>
          
          {submissaoDiferencas && (
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="grid grid-cols-5 gap-4">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{submissaoDiferencas.resumo.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{submissaoDiferencas.resumo.por_magnitude.Baixa}</p>
                  <p className="text-xs text-slate-500">Baixa</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-600">{submissaoDiferencas.resumo.por_magnitude.Média}</p>
                  <p className="text-xs text-slate-500">Média</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600">{submissaoDiferencas.resumo.por_magnitude.Alta}</p>
                  <p className="text-xs text-slate-500">Alta</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{submissaoDiferencas.resumo.por_magnitude.Crítica}</p>
                  <p className="text-xs text-slate-500">Crítica</p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button onClick={handleAprovarTodasIA} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aprovar Todas Sugeridas IA ({submissaoDiferencas.resumo.aprovadas_ia})
                </Button>
                <Button onClick={handleAplicarAlteracoes} className="bg-[#0066CC] hover:bg-[#0052a3]">
                  <Send className="w-4 h-4 mr-2" />
                  Aplicar Alterações Aprovadas
                </Button>
              </div>
              
              {/* Differences Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Diferenças Detectadas</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Campo</TableHead>
                        <TableHead>Anterior</TableHead>
                        <TableHead>Novo</TableHead>
                        <TableHead className="text-center">Variação</TableHead>
                        <TableHead>Magnitude</TableHead>
                        <TableHead>IA Recomenda</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submissaoDiferencas.diferencas.map((dif) => (
                        <TableRow key={dif.id} className="hover:bg-slate-50">
                          <TableCell>
                            <p className="font-medium text-sm">{dif.campo_afetado}</p>
                            <p className="text-xs text-slate-500">{dif.categoria_campo}</p>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{String(dif.valor_anterior)}</TableCell>
                          <TableCell className="font-mono text-sm font-bold">{String(dif.valor_novo)}</TableCell>
                          <TableCell className="text-center">
                            {dif.percentagem_variacao != null && (
                              <span className={`text-sm font-medium ${dif.percentagem_variacao > 20 ? 'text-red-600' : 'text-slate-600'}`}>
                                {dif.percentagem_variacao > 0 ? '+' : ''}{dif.percentagem_variacao}%
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge style={{ backgroundColor: MAGNITUDE_COLORS[dif.magnitude] }} className="text-white">
                              {dif.magnitude}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {dif.validacao_ia && (
                              <div>
                                <Badge variant="outline" className={
                                  dif.validacao_ia.recomendacao === 'Aprovar' ? 'border-green-500 text-green-600' :
                                  dif.validacao_ia.recomendacao === 'Rejeitar' ? 'border-red-500 text-red-600' : 
                                  'border-yellow-500 text-yellow-600'
                                }>
                                  {dif.validacao_ia.recomendacao}
                                </Badge>
                                <p className="text-xs text-slate-500 mt-1 max-w-[150px] truncate" title={dif.validacao_ia.justificacao}>
                                  {dif.validacao_ia.justificacao}
                                </p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              dif.status === 'Aprovada' ? 'bg-green-100 text-green-800' :
                              dif.status === 'Rejeitada' ? 'bg-red-100 text-red-800' :
                              'bg-slate-100'
                            }>
                              {dif.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {dif.status === 'Pendente' && (
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleAprovarDiferenca(dif.id)}
                                  className="text-green-600 hover:bg-green-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleRejeitarDiferenca(dif.id)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <AlertTriangle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Builder Modal */}
      <Dialog open={formBuilderModalOpen} onOpenChange={setFormBuilderModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="form-builder-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-purple-600" />
              Form Builder: {selectedCampanha?.nome}
            </DialogTitle>
            <DialogDescription>
              Configure os campos personalizados que serão incluídos no formulário de atualização
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <FormBuilder
              campos={camposPersonalizados}
              onChange={setCamposPersonalizados}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormBuilderModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={saveFormBuilderCampos} 
              className="bg-[#0066CC] hover:bg-[#0052a3]"
              disabled={formLoading}
            >
              {formLoading ? 'A guardar...' : 'Guardar Campos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
